import { INavLinkGroup } from '@fluentui/react';
import { defaultPalette, Nav } from '@h2oai/ui-kit';
import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { RoutePaths } from '../pages/Routes';
import { useHasReviewerRole, useQueryPendingFeatureSets, useQueryPendingReviews } from './featurestore.hooks';
import { ContentsTitle } from './models';
import { getErrorMessageBarMessage } from './utils';

const getMenuItems = ({
  hasReviewerRole,
  history,
  pendingFeatureSetCount,
  pendingReviewCount,
}: {
  hasReviewerRole?: boolean;
  history: ReturnType<typeof useHistory>;
  pendingFeatureSetCount: number;
  pendingReviewCount: number;
}): INavLinkGroup[] => {
  const menuItems: INavLinkGroup[] = [
    {
      links: [
        {
          name: ContentsTitle.home,
          key: RoutePaths.FEATURESTORE_ROOT,
          url: RoutePaths.FEATURESTORE_ROOT,
          icon: 'Home',
          onClick: (e) => {
            history.push(RoutePaths.FEATURESTORE_ROOT);
            e?.preventDefault();
          },
        },
        {
          name: ContentsTitle.projects,
          url: RoutePaths.FEATURESTORE_PROJECTS,
          key: ContentsTitle.projects,
          icon: 'DocumentSet',
          onClick: (e) => {
            history.push(RoutePaths.FEATURESTORE_PROJECTS);
            e?.preventDefault();
          },
          links: [
            {
              name: ContentsTitle.allProjects,
              url: RoutePaths.FEATURESTORE_PROJECTS,
              key: RoutePaths.FEATURESTORE_PROJECTS,
              onClick: (e) => {
                history.push(RoutePaths.FEATURESTORE_PROJECTS);
                e?.preventDefault();
              },
            },
            {
              name: ContentsTitle.myProjects,
              url: RoutePaths.FEATURESTORE_MY_PROJECTS,
              key: RoutePaths.FEATURESTORE_MY_PROJECTS,
              onClick: (e) => {
                history.push(RoutePaths.FEATURESTORE_MY_PROJECTS);
                e?.preventDefault();
              },
            },
          ],
          isExpanded: true,
        },
        {
          name: ContentsTitle.featureSets,
          url: RoutePaths.FEATURESTORE_FEATURE_SETS,
          key: ContentsTitle.featureSets,
          icon: 'FormLibraryMirrored',
          onClick: (e) => {
            history.push(RoutePaths.FEATURESTORE_FEATURE_SETS);
            e?.preventDefault();
          },
          links: [
            {
              name: ContentsTitle.allFeatureSets,
              url: RoutePaths.FEATURESTORE_FEATURE_SETS,
              key: RoutePaths.FEATURESTORE_FEATURE_SETS,
              onClick: (e) => {
                history.push(RoutePaths.FEATURESTORE_FEATURE_SETS);
                e?.preventDefault();
              },
            },
            {
              name: ContentsTitle.myFeatureSets,
              url: RoutePaths.FEATURESTORE_MY_FEATURE_SETS,
              key: RoutePaths.FEATURESTORE_MY_FEATURE_SETS,
              onClick: (e) => {
                history.push(RoutePaths.FEATURESTORE_MY_FEATURE_SETS);
                e?.preventDefault();
              },
            },
            {
              name: `${ContentsTitle.pendingFeatureSets}${
                pendingFeatureSetCount > 0 ? ` (${pendingFeatureSetCount})` : ''
              }`,
              iconProps:
                pendingFeatureSetCount > 0
                  ? {
                      iconName: 'LocationDot',
                      styles: { root: { color: defaultPalette.red500 } },
                    }
                  : undefined,
              url: RoutePaths.FEATURESTORE_PENDING_FEATURE_SETS,
              key: RoutePaths.FEATURESTORE_PENDING_FEATURE_SETS,
              onClick: (e) => {
                history.push(RoutePaths.FEATURESTORE_PENDING_FEATURE_SETS);
                e?.preventDefault();
              },
            },
          ],
          isExpanded: true,
        },
        {
          name: ContentsTitle.jobs,
          url: RoutePaths.FEATURESTORE_JOBS,
          key: ContentsTitle.jobs,
          icon: 'ProcessMetaTask',
          onClick: (e) => {
            history.push(RoutePaths.FEATURESTORE_JOBS);
            e?.preventDefault();
          },
          links: [
            {
              name: ContentsTitle.jobs,
              url: RoutePaths.FEATURESTORE_JOBS,
              key: RoutePaths.FEATURESTORE_JOBS,
              onClick: (e) => {
                history.push(RoutePaths.FEATURESTORE_JOBS);
                e?.preventDefault();
              },
            },
          ],
          isExpanded: true,
        },
        {
          name: ContentsTitle.accessControl,
          url: RoutePaths.FEATURESTORE_PROJECT_PERMISSIONS,
          key: ContentsTitle.accessControl,
          icon: 'Permissions',
          onClick: (e) => {
            history.push(`${RoutePaths.FEATURESTORE_PROJECT_PERMISSIONS}?tab=${ContentsTitle.managePermissions}`);
            e?.preventDefault();
          },
          links: [
            {
              name: ContentsTitle.projectPermissions,
              url: RoutePaths.FEATURESTORE_PROJECT_PERMISSIONS,
              key: RoutePaths.FEATURESTORE_PROJECT_PERMISSIONS,
              onClick: (e) => {
                history.push(`${RoutePaths.FEATURESTORE_PROJECT_PERMISSIONS}?tab=${ContentsTitle.managePermissions}`);
                e?.preventDefault();
              },
            },
            {
              name: ContentsTitle.featureSetPermissions,
              url: RoutePaths.FEATURESTORE_FEATURE_SETS_PERMISSIONS,
              key: RoutePaths.FEATURESTORE_FEATURE_SETS_PERMISSIONS,
              onClick: (e) => {
                history.push(
                  `${RoutePaths.FEATURESTORE_FEATURE_SETS_PERMISSIONS}?tab=${ContentsTitle.managePermissions}`
                );

                e?.preventDefault();
              },
            },
            ...(hasReviewerRole
              ? [
                  {
                    name: `${ContentsTitle.pendingReviews}${pendingReviewCount > 0 ? ` (${pendingReviewCount})` : ''}`,
                    iconProps:
                      pendingReviewCount > 0
                        ? {
                            iconName: 'LocationDot',
                            styles: { root: { color: defaultPalette.red500 } },
                          }
                        : undefined,
                    url: RoutePaths.FEATURESTORE_PENDING_REVIEWS,
                    key: RoutePaths.FEATURESTORE_PENDING_REVIEWS,
                    onClick: (e?: React.MouseEvent) => {
                      history.push(RoutePaths.FEATURESTORE_PENDING_REVIEWS);
                      e?.preventDefault();
                    },
                  },
                ]
              : []),
          ],
          isExpanded: true,
        },
      ],
    },
  ];
  return menuItems;
};

const getPathName = (pathname: string) => {
  if (pathname.startsWith(RoutePaths.FEATURESTORE_PROJECT)) {
    return RoutePaths.FEATURESTORE_PROJECTS;
  }
  if (pathname.startsWith(RoutePaths.FEATURESTORE_FEATURE_SET)) {
    return RoutePaths.FEATURESTORE_FEATURE_SETS;
  }
  if (pathname.startsWith(RoutePaths.FEATURESTORE_PENDING_FEATURE_SET)) {
    return RoutePaths.FEATURESTORE_PENDING_FEATURE_SETS;
  }
  if (pathname.startsWith(RoutePaths.FEATURESTORE_PENDING_REVIEWS)) {
    return RoutePaths.FEATURESTORE_PENDING_REVIEWS;
  }
  if (pathname === RoutePaths.FEATURESTORE_SEARCH_RESULTS) {
    return RoutePaths.FEATURESTORE_ROOT;
  }
  return pathname;
};

export function FeatureStoreNavigation() {
  const history = useHistory();

  const hasReviewerRole = useHasReviewerRole();
  const pendingReviewsQuery = useQueryPendingReviews({
    enabled: hasReviewerRole,
    meta: {
      toastError: (err: unknown) =>
        getErrorMessageBarMessage(err, `An error has occurred while loading pending review count: `).message,
    },
  });
  const pendingReviewCount = pendingReviewsQuery.data?.entries?.length || 0;
  const pendingFeatureSetQuery = useQueryPendingFeatureSets({
    meta: {
      toastError: (err: unknown) =>
        getErrorMessageBarMessage(err, `An error has occurred while loading pending feature set count: `).message,
    },
  });
  const pendingFeatureSetCount = pendingFeatureSetQuery.data?.entries?.length || 0;
  const { pathname } = useLocation();

  return (
    <Nav
      styles={{ linkText: { overflow: 'visible' } }}
      groups={getMenuItems({
        hasReviewerRole,
        history,
        pendingReviewCount,
        pendingFeatureSetCount,
      })}
      selectedKey={getPathName(pathname)}
    />
  );
}
